import FrameButton from "@/components/buttons/FrameButton";
import LoginModal from "@/components/modals/LoginModal.tsx";
import useThemePreference from "@/hooks/useThemePreference";
import { store } from "@/redux";
import { IStore } from "@/redux/defaultStore";
import { decrementModalCount, toggleSidebar } from "@/redux/meta/metaActions";
import { ThemePreference } from "@/types/Theme";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDateInEst } from "@/utils/getDateInEst";
import getAnonymousSpin from "@/utils/getAnonymousSpin";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import DevourTopNav from "@/components/DevourTopNav";
import GoVipOWDualRewardingGames from "@/components/goVip/GoVipOWDualRewardingGames";
import GoVipOWTimeBasedGames from "@/components/goVip/GoVipOWTimeBasedGames";
import { isDesktop, isTablet } from "react-device-detect";
import useWindowSize from "@/hooks/useWindowSize";
import OWComingSoonGames from "@/components/devourPlay/OWComingSoonGames";
import classNames from "classnames";
import OWFooter from "@/components/devourPlay/OWFooter";
import OWDiscoverEarnXPModal from "@/components/devourPlay/OWDiscoverEarnXPModal";
import { OverwolfLevelRewardsCarousel } from "../overwolf/components/OverwolfLevelRewardsCarousel";
import OwIntroductionModal from "@/components/devourPlay/OWIntroductionModal";
import GoVipSpin2WinModal from "@/components/modals/GoVipSpin2WinModal";
import getConfig from "@/utils/getConfig";
import { SiteConfigurationsApi, SiteConfigurationKey, SiteConfiguration } from "@devour/client";
import { LandingPageImagesCarousel } from "../landing/LandingPageImagesCarousel";
import { LandingPageFaq } from "../landing/LandingPageFaq";

const DEVOUR_CDN = import.meta.env.VITE_CDN_URL;

const SOCIALS = [
    {
        name: "lp-join-discord.webp",
        link: "https://discord.com/invite/devourplay",
        alt: "discord-icon",
    },
    {
        name: "lp-join-facebook.webp",
        link: "https://www.facebook.com/GoDevour",
        alt: "facebook-icon",
    },
    {
        name: "lp-join-instagram.webp",
        link: "https://www.instagram.com/go_devour/",
        alt: "instagram-icon",
    },
    {
        name: "lp-join-telegram.webp",
        link: "https://t.me/GoDevour",
        alt: "telegram-icon",
    },
    {
        name: "lp-join-twitter.webp",
        link: "https://x.com/GoDevour",
        alt: "twitter-icon",
    },
];

export default function DevourPlayDiscoverPage() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const anonymousSpin = useSelector((store: IStore) => store.metaStore.anonymousSpin);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const dispatch = useDispatch();
    const windowSize = useWindowSize()[0];
    const isOnMobile = !isDesktop && !isTablet || windowSize < 600;
    const [showWaysToEarnXPModal, setShowWaysToEarnXPModal] = useState<boolean>(false);
    const [showIntroModal, setShowIntroModal] = useState<boolean>(false);
    const [showSpinModal, setShowSpinModal] = useState<boolean>(false);
    const [isSpinAvailable, setIsSpinAvailable] = useState<boolean>(false);
    const [announcementModalConfig, setAnnouncementModalConfig] = useState<SiteConfiguration | null>(null);
    const overwolfAppUrl = "https://www.overwolf.com/app/Devour-DevourPlay";
    const { data: userProfileData } = useGetUserProfile(fullToken);

    // Close the sidebar menu if open
    useEffect(() => {
        dispatch(toggleSidebar(false));
    }, []);

    useEffect(() => {
        void getAnnouncementModalConfig();
        dispatch(toggleSidebar(false));
    }, []);

    // Check if spin is available (same logic as GoVipSpin2Win)
    useEffect(() => {
        if (currentUser) {
            const lastSpinDate = currentUser?.user?.lastSpin
                ? getDateInEst(new Date(currentUser?.user?.lastSpin))
                : null;

            const currentDate = new Date().toLocaleString("en-US", {timeZone: "America/New_York"});
            const startOfToday = new Date(currentDate);
            startOfToday.setHours(0, 0, 0, 0);
            const spinEnabled: boolean = !lastSpinDate || lastSpinDate < startOfToday;
            setIsSpinAvailable(spinEnabled);
        } else {
            // For anonymous users, check if they've already spun today
            const anonymousSpinData = getAnonymousSpin();
            const canSpinAnonymous = !anonymousSpinData;
            setIsSpinAvailable(canSpinAnonymous);
        }
    }, [currentUser?.user?.lastSpin, currentUser, anonymousSpin]);

    async function getAnnouncementModalConfig() {
        try {
            const modalConfig = await new SiteConfigurationsApi(getConfig(fullToken)).getSiteConfiguration({
                configKey: SiteConfigurationKey.OWANNOUNCEMENTMODAL,
            });
            setAnnouncementModalConfig(modalConfig);
        } catch (e) {
            console.error("Error fetching announcement modal config", e);
        }
    }

    const getHeaderBannerImageUrl = () => {
        let screenSize: string;

        if (isOnMobile) {
            screenSize = "SM";
        } else {
            screenSize = "LG";
        }
        return `${DEVOUR_CDN}/images/OW-Discover-Header-Bnr-${screenSize}.webp`;
    };

    const handlePlayOrLogin = (fullToken) => {
        if (fullToken) {
            toggleSpinModal();
        } else {
            setIsLoginModalOpen(true);
        }
    };

    // Render the "Ready to Devour & Play" Banner
    const renderDevourAndPlayBanner = (className?: string) => {
        if (!announcementModalConfig?.isWithinValidDate) {
            return <div></div>;
        }
        return (
            <div className={classNames("overwolf-discover-page_infos_col2", className)}>
                <div className="overwolf-discover-page_infos_col2_img">
                    <img src={announcementModalConfig?.asset?.cdnUrl} alt="" />
                </div>
                <div className="overwolf-discover-page_infos_col2_text">
                    <span className="col2-title">{announcementModalConfig?.title}</span>
                    <span className="col2-caption">{announcementModalConfig?.description}</span>
                    {announcementModalConfig?.url && <FrameButton
                        size="pill"
                        color="white-drop-shadow-devour"
                        className="overwolf-discover-page_infos_col2_btn"
                        onClick={() => {
                            window.open(announcementModalConfig?.url, "_self");
                        }}
                    >
                        Let's Go!
                    </FrameButton>}
                </div>
            </div>
        );
    };

    function toggleSpinModal(): void {
        dispatch(decrementModalCount());
        setShowSpinModal(!showSpinModal);
    }

    const handleNavigateToOWApp = () => {
        window.open(overwolfAppUrl, "_blank");
    };

    return (
        <>
            <GoVipSpin2WinModal isOpen={showSpinModal} toggle={toggleSpinModal} />

            <OwIntroductionModal isOpen={showIntroModal} onClose={() => setShowIntroModal(false)} />

            <OWDiscoverEarnXPModal isOpen={showWaysToEarnXPModal} onClose={() => setShowWaysToEarnXPModal(false)} />

            <div className="devourplay-landing-page">
                <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />

                {/* Show DevourTopNav when user is logged in */}
                {fullToken && <DevourTopNav text="Discover" />}

                {/* NON HEADER - REST OF THE PAGE CONTENT */}
                <div className="devourplay-landing-page_container">
                    <div className="devourplay-landing-page_infos">
                        {/* COLUMN 1 - VIDEO COLUMN */}
                        <div className="devourplay-landing-page_infos_col1">
                            <div className="devourplay-landing-page_infos_col1_heading">
                                {/* Show DevourPlay logo on mobile when not logged in */}
                                {isOnMobile && !fullToken &&
                                    <img
                                        src={`${DEVOUR_CDN}/images/devourplay-logo2.png`}
                                        alt="DevourPlay"
                                        className="devourplay-landing-page_mobile-logo"
                                        style={{ width: "200px", marginBottom: "1.5rem" }}
                                    />
                                }
                                <h2>
                                    PLAY.
                                    <br />
                                    EARN.
                                    <br />
                                    REPEAT.
                                </h2>
                                <p>
                                    DevourPlay lets you rack up XP, crush quests, and unlock real rewards — just for
                                    playing your favorite games. Power up your sessions, climb the ranks, and claim
                                    what's yours.
                                </p>
                                <div className="devourplay-landing-page_buttons" style={isOnMobile ? { width: "100%", padding: "0 1rem" } : {}}>
                                    {/* Show Login button when not authenticated, or Spin button only when authenticated AND spin available */}
                                    {!fullToken
                                        ? <FrameButton
                                            color="devourplay-gradient"
                                            size="normal"
                                            className="devourplay-landing-page_login-btn"
                                            onClick={() => handlePlayOrLogin(fullToken)}
                                        >
                                            Login
                                        </FrameButton>
                                        : isSpinAvailable &&
                                            <FrameButton
                                                color="devourplay-gradient"
                                                size="normal"
                                                className="devourplay-landing-page_login-btn"
                                                onClick={() => handlePlayOrLogin(fullToken)}
                                            >
                                                Spin the Wheel
                                            </FrameButton>

                                    }
                                    {!isOnMobile && !isOnOverwolf &&
                                        <FrameButton
                                            color="white-outline"
                                            size="normal"
                                            className="devourplay-landing-page_download-btn"
                                            onClick={handleNavigateToOWApp}
                                        >
                                            Download DevourPlay
                                        </FrameButton>
                                    }
                                </div>
                            </div>

                            {isOnMobile && renderDevourAndPlayBanner()}
                        </div>

                        {/* COLUMN 2 (only desktop) - How to Devour and Play Banner */}
                        {renderDevourAndPlayBanner(classNames({ hide: isOnMobile }))}
                    </div>

                    {/* Game Carousel */}
                    <div className="devourplay-landing-page_game-carousel">
                        <LandingPageImagesCarousel />
                    </div>

                    <div
                        className="devourplay-landing-page_infos_col1_xp"
                        onClick={() => setShowWaysToEarnXPModal(true)}
                    >
                        <div className="devourplay-landing-page_infos_col1_xp_text">
                            <span className="xp-title">5 Ways to Earn XP</span>
                            <span className="xp-caption">Discover How to Earn XP</span>
                        </div>
                        <div
                            className="right"
                            style={{
                                backgroundImage: `url(${DEVOUR_CDN}/images/OW-Discover-Right.png)`,
                            }}
                        />
                    </div>
                    {/* TEXTURE */}
                    <div
                        className="devourplay-landing-page_texture"
                        style={{
                            backgroundImage: `url(${DEVOUR_CDN}/images/devourplay-texture.webp)`,
                        }}
                    >
                        <img
                            src={`${DEVOUR_CDN}/images/OW-Discover-Down.png`}
                            alt="discover-page-down"
                            className="down"
                        />
                    </div>

                    {/* LEVEL REWARDS SECTION */}
                    {/* TODO: Implement Level Rewards Section here*/}
                    <div className="devourplay-landing-page_lvl-rewards">
                        <div className="devourplay-landing-page_lvl-rewards_heading">
                            <h2>LEVEL REWARDS</h2>
                            <p>
                                Level up to get food discounts, Fuel tokens, merch items, or loot boxes with epic
                                prizes!
                            </p>
                        </div>
                        <OverwolfLevelRewardsCarousel />
                    </div>

                    {/* GAMES SECTION */}
                    <div className="go-vip-overwolf">
                        <GoVipOWTimeBasedGames isOWDiscoverPage />

                        <GoVipOWDualRewardingGames isOWDiscoverPage />

                        <OWComingSoonGames />
                    </div>

                </div>

                {/* Get the app section - moved outside container for full width */}
                {!isOnOverwolf &&
                <div className="landing landing_get-the-app">
                    <div className="landing_get-the-app_heading">
                        <h5>GET THE APP</h5>
                        <p>Download DevourPlay on Overwolf</p>
                        <FrameButton onClick={handleNavigateToOWApp} color="white-outline" size="narrow">
                            <img
                                src={`${DEVOUR_CDN}/images/white-overwolf-icon.webp`}
                                alt="Overwolf logo"
                            />{" "}
                            Overwolf
                        </FrameButton>
                    </div>
                    <div className="landing_get-the-app_showcase">
                        <img
                            src={`${DEVOUR_CDN}/images/lp-ow-showcase.webp`}
                            alt="overwolf showcase"
                        />
                    </div>
                    <img
                        src={`${DEVOUR_CDN}/images/lp-3d-delivery.webp`}
                        className="landing_get-the-app_left-texture"
                        alt="left texture"
                    />
                    <img
                        src={`${DEVOUR_CDN}/images/lp-3d-food.webp`}
                        className="landing_get-the-app_right-texture"
                        alt="right texture"
                    />
                </div>
                }

                <div className="landing">
                    <div className="landing_paddings">
                        {/* Got questions section */}
                        <div className="landing_got-questions">
                            <div className="landing_got-questions_heading">
                                <h5>
                                    GOT <span>QUESTIONS</span>?
                                </h5>
                                <p>Frequently Asked Questions</p>
                            </div>
                            <LandingPageFaq />
                        </div>

                        {/* Join the fam section */}
                        <div className="landing_join-the-fam">
                            <div className="landing_join-the-fam_heading">
                                <h5>JOIN OUR FAM</h5>
                                <p>Welcome to the good-vibes-only community :)</p>
                            </div>
                            <div className="landing_join-the-fam_social-icons">
                                {SOCIALS.map((socialData, index) =>
                                    <a
                                        key={index}
                                        href={socialData.link}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <img
                                            src={`${DEVOUR_CDN}/images/${socialData.name}`}
                                            alt={socialData.alt}
                                        />
                                    </a>)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="devourplay-landing-page">
                    {/* FOOTER */}
                    <div className="devourplay-landing-page_footer">{/* <OWFooter/> */}</div>
                </div>
            </div>
        </>
    );
}